import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { classNames } from "../utils";

interface ChipGroupProps {
  options: string[];
  selected?: string[];
  onChange?: (selected: string[], indices: number[]) => void;
  disabled?: boolean;
  maxWidth?: string; // Tailwind class.
  colors?: {
    border?: string;
    background?: string;
    hover?: string;
    selected?: string;
  };
  className?: string;
}

const ChipGroup = ({
  options,
  selected = [],
  onChange,
  disabled = false,
  maxWidth,
  colors,
  className,
}: ChipGroupProps) => {
  const [internalSelected, setInternalSelected] = useState<string[]>(selected);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const prevSelectedRef = useRef<string[]>(selected);

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => a.localeCompare(b)),
    [options]
  );

  const formatOptionText = useCallback((option: string) => {
    return option
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }, []);

  // Update internal state when selected prop changes (only if different)
  useEffect(() => {
    if (JSON.stringify(selected) !== JSON.stringify(prevSelectedRef.current)) {
      setInternalSelected(selected);
      prevSelectedRef.current = selected;
    }
  }, [selected]);

  // Update chip refs array when options change
  useEffect(() => {
    chipRefs.current = chipRefs.current.slice(0, sortedOptions.length);
  }, [sortedOptions.length]);

  const handleChipClick = useCallback(
    (option: string, index: number) => {
      if (disabled) return;

      const newSelected = internalSelected.includes(option)
        ? internalSelected.filter((item) => item !== option)
        : [...internalSelected, option];

      const newIndices = newSelected.map((item) => options.indexOf(item));

      setInternalSelected(newSelected);
      onChange?.(newSelected, newIndices);
    },
    [disabled, internalSelected, onChange, options]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return;

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev + 1;
            return next >= sortedOptions.length ? 0 : next;
          });
          break;
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev - 1;
            return next < 0 ? sortedOptions.length - 1 : next;
          });
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < sortedOptions.length) {
            handleChipClick(sortedOptions[focusedIndex], focusedIndex);
          }
          break;
        case "Escape":
          setFocusedIndex(-1);
          break;
      }
    },
    [disabled, focusedIndex, sortedOptions, handleChipClick]
  );

  // Focus management
  useEffect(() => {
    if (focusedIndex >= 0 && chipRefs.current[focusedIndex]) {
      chipRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex]);

  // Announce selection changes to screen readers
  useEffect(() => {
    if (internalSelected.length > 0) {
      const announcement = `Selected: ${internalSelected.join(", ")}`;
      // Create a live region for screen reader announcements
      const liveRegion = document.createElement("div");
      liveRegion.setAttribute("aria-live", "polite");
      liveRegion.setAttribute("aria-atomic", "true");
      liveRegion.className = "sr-only";
      liveRegion.textContent = announcement;
      document.body.appendChild(liveRegion);

      // Clean up after announcement
      setTimeout(() => {
        document.body.removeChild(liveRegion);
      }, 1000);
    }
  }, [internalSelected]);

  const getChipClasses = (option: string, index: number) => {
    const isSelected = internalSelected.includes(option);
    const isFocused = focusedIndex === index;

    return classNames(
      "inline-flex items-center px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200 flex-shrink-0 border",
      colors?.border ? colors?.border : "border-gray-300",
      isSelected
        ? colors?.selected || "!bg-[#233253] !text-white !border-[#233253]"
        : classNames(colors?.background || "bg-white", "text-gray-700"),
      !isSelected && !disabled ? colors?.hover || "hover:bg-gray-200" : "",
      disabled ? "opacity-50 cursor-not-allowed" : "",
      isFocused && !isSelected ? "ring-2 ring-blue-500 ring-offset-2" : ""
    );
  };

  return (
    <div className={classNames("w-full mb-4", className ?? "")}>
      <div
        ref={containerRef}
        className={classNames(
          "flex flex-wrap gap-2 min-w-0",
          disabled ? "pointer-events-none" : "",
          maxWidth || ""
        )}
        onKeyDown={handleKeyDown}
        role="group"
        aria-label="Chip selection group"
        tabIndex={disabled ? -1 : 0}
      >
        {sortedOptions.map((option, index) => (
          <button
            key={option}
            ref={(el) => {
              chipRefs.current[index] = el;
            }}
            type="button"
            className={getChipClasses(option, index)}
            onClick={() => handleChipClick(option, index)}
            disabled={disabled}
            aria-pressed={internalSelected.includes(option)}
          >
            {formatOptionText(option)}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ChipGroup;
